import * as React from "react"
import {Loader} from "semantic-ui-react"
import {Auth0Config, Auth0ConfigFragFragment} from "../graphql/queries"
import {GraphQL} from "../graphql"
import {ReactNode} from "react"
import {parseQueryString, unparseQueryString} from "../urlLocale"
import {undefinedToNull} from "../tsutil"
import {Location} from "history"

type Auth0Screen = "login" | "signUp" | "forgotPassword"

interface OwnProps {
  email: string | null
  returnUrl?: string | null
  initialScreen: Auth0Screen
  className?: string
  children?: ReactNode
}

interface QueryProps {
  auth0Config: Auth0ConfigFragFragment
}

type Props = OwnProps & QueryProps

export function redirectTo(url: string) {
  // Supposedly assigning directly to window.location.href best simulates
  // the user clicking on an "<a href=..>..</a>" link.
  window.location.href = url;
}

function getReturnUrl(returnUrl: string | null = null): string {
  if (returnUrl != null) {
    return returnUrl;
  }
  const location = new URL(window.location.href);
  // If we log in from the home page, go to the list of bookings.
  if (location.pathname === "/") {
    location.pathname = "/bookings";
  }
  return location.href;
}

export function auth0Url(
  auth0Config: Auth0ConfigFragFragment,
  email: string | null,
  initialScreen: Auth0Screen,
  allowLogin: boolean = false,
  allowSignUp: boolean = false,
  newBookerSecret: string | null = null,
  returnUrl: string | null = null,
): string {
  return (
    "https://" +
    auth0Config.domain +
    "/authorize?" +
    [
      "client_id=" + encodeURIComponent(auth0Config.clientId),
      "redirect_uri=" + encodeURIComponent(auth0Config.loginCallbackUrl),
      "scope=" + encodeURIComponent("openid email profile"),
      "response_type=code",
      "state=" +
        encodeURIComponent(
          JSON.stringify({
            returnUrl: getReturnUrl(returnUrl),
            nonce: auth0Config.nonce,
            newBookerSecret,
          }),
        ),
      // Extra params for our hosted page
      "email=" + encodeURIComponent(email || ""),
      "initialScreen=" + encodeURIComponent(initialScreen),
      "allowLogin=" + (allowLogin ? "1" : ""),
      "allowSignUp=" + (allowSignUp ? "1" : ""),
      "title=C U Soon",
    ].join("&")
  )
}

class Auth0LockRedirect extends React.Component<Props> {
  componentDidMount() {
    const {email, initialScreen, auth0Config, returnUrl} = this.props;
    // Always pass a returnUrl to auth0Url() here, otherwise it will redirect back to this redirect
    // and get stuck in a redirect loop.
    const returnUrl2 = returnUrl == null ? "/bookings" : returnUrl;
    window.location.replace(auth0Url(auth0Config, email, initialScreen, false, false, null, returnUrl2))
  }

  render() {
    return "You are being redirected";
  }
}

export function getUrlEmailAndReturnUrl(location: Location) {
  const query = new Map(parseQueryString(location.search));
  return {
    email: undefinedToNull(query.get("email")),
    returnUrl: undefinedToNull(query.get("returnUrl")),
  };
}

function auth0RedirectUrl(props: OwnProps) {
  const query = unparseQueryString([
    ["email", props.email],
    ["returnUrl", getReturnUrl(props.returnUrl)],
  ]);
  switch (props.initialScreen) {
    case "forgotPassword":
      return "/forgot-password" + query;
    case "login":
      return "/log-in" + query;
    case "signUp":
      return "/sign-up" + query;
  }
}

export function Auth0LockLink(props: OwnProps) {
  return (
    <a
      // The way Semantic UI React's special "as" property works is by forwarding
      // "className" and "children" props to the component which it must forward onto
      // the element that it renders as.
      className={props.className}
      children={props.children}
      href={auth0RedirectUrl(props)}
      // This is to prevent search engines from following the URL and giving people URLs
      // that log them in/out etc
      rel="nofollow"
    />
  )
}

function WithApollo(props: OwnProps) {
  return (
    <GraphQL.Query
      query={Auth0Config}
      variables={{}}
      render={({data}) => {
        if (data) {
          return <Auth0LockRedirect {...props} auth0Config={data.auth0Config} />
        } else {
          return <Loader inline={true} size="tiny" active />
        }
      }}
    />
  )
}

export {WithApollo as Auth0LockRedirect}



// WEBPACK FOOTER //
// ./src/components/Auth0LockLink.tsx